/**
 * This file should be refactored and moved to the EdroneDatepicker directory,
 * after the EdroneDatepicker component is moved from ECL to control.
 *
 * @see https://edrone.atlassian.net/browse/COOLD-114
 */

import dayjs from "dayjs";
import { getUnix, isValidDate } from "vue-app/utils/functions";
import { ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

dayjs.extend(isSameOrAfter);

export {
  useTimestampQueryParams,
  getDefaultDateRange,
  getDefaultDateRangeWithCurrentDay,
  parseDatepickerRangeFromQueryParams,
  mapUnixToLocalTimeDate,
  disabledBeforeToday,
  disabledAfterToday,
  disabledWithToday,
  getUnixStartOfDayPlusOneSecond,
  getUnixEndOfDay,
  getDateMonthAgo,
  getDateInLocalTimezoneFromAccountTimezone,
  getDateInAccountTimezoneFromLocalTimezone,
};

export type ReturnParseDatepickerRange = {
  dateFrom: Date;
  dateTo: Date;
};

/**
 * It should be used together with the EdroneDatepicker.
 *
 * To work successfully with router query params in Vue 2, in `routes.js` you need to:
 * 1. add a `name` to the route you want to work with (the same as path but with "-" instead of "/")
 * 2. add `skipGlobalLoader: true`
 */
function useTimestampQueryParams() {
  const router = useRouter();
  const route = useRoute();
  const queryParams = route.query;
  const defaultDateRange = getDefaultDateRange();
  const timestampRange = ref(parseTimestampRange(queryParams, defaultDateRange));

  function updateTimestampQueryParams(dates: { dateFrom: Date; dateTo: Date }) {
    const dateFrom = getUnixStartOfDayPlusOneSecond(dates.dateFrom).toString();
    const dateTo = getUnixEndOfDay(dates.dateTo).toString();

    timestampRange.value = {
      dateFrom,
      dateTo,
    };

    window.sessionStorage.setItem("EdroneDatepicker", JSON.stringify([dateFrom, dateTo]));

    router.push({
      name: route.name ?? "",
      query: {
        date_from: dateFrom,
        date_to: dateTo,
      },
    });
  }

  return { timestampRange, updateTimestampQueryParams };
}

function parseTimestampRange(queryParams: { date_from?: string; date_to?: string }, defaultDateRange: Date[]) {
  return {
    dateFrom: isValidDate(queryParams.date_from)
      ? (queryParams.date_from as string)
      : window.sessionStorage.getItem("EdroneDatepicker") !== null
      ? JSON.parse(window.sessionStorage.getItem("EdroneDatepicker") ?? "")[0]
      : getUnix(defaultDateRange[0]).toString(),
    dateTo: isValidDate(queryParams.date_to)
      ? (queryParams.date_to as string)
      : window.sessionStorage.getItem("EdroneDatepicker") !== null
      ? JSON.parse(window.sessionStorage.getItem("EdroneDatepicker") ?? "")[1]
      : getUnix(defaultDateRange[1]).toString(),
  };
}

function parseDatepickerRangeFromQueryParams(queryParams: Record<string, string>): ReturnParseDatepickerRange {
  const defaultDateRange = getDefaultDateRange();

  return {
    dateFrom: isValidDate(queryParams.dateFrom)
      ? mapUnixToLocalTimeDate(Number(queryParams.dateFrom))
      : defaultDateRange[0],
    dateTo: isValidDate(queryParams.dateTo) ? mapUnixToLocalTimeDate(Number(queryParams.dateTo)) : defaultDateRange[1],
  };
}

/**
 * This function is needed to parse timestamp from query params into the EdroneDatepicker component and is responsible for correct displaying the date.
 * The EdroneDatepicker based on new Date() object, which is in local timezone taken from the browser. The timestamp from query params is in UTC.
 * If the EdroneDatepicker would be based on UTC timezone, the date would be displayed incorrectly.
 *
 * Example:
 * date_from in query params: 1680652801 // GMT: Wednesday, April 5, 2023 12:00:01 AM
 * But the local timezone of the browser can be GMT-2,
 * so the EdroneDatepicker would display: Tuesday, April 4, 2023 10:00:01 PM // as a result of new Date(1680652801 * 1000)
 * So this would simply leads to confusion while displaying the date in the EdroneDatepicker.
 * That's why we can't simply use new Date() in the EdroneDatepicker component.
 * We need to add (or subtract) the timezone offset from the new Date() object.
 */
function mapUnixToLocalTimeDate(unix: number) {
  const utcOffset = dayjs(unix * 1000).utcOffset();

  if (utcOffset > 0) {
    return dayjs(unix * 1000)
      .subtract(utcOffset, "minutes")
      .toDate();
  }
  if (utcOffset < 0) {
    return dayjs(unix * 1000)
      .add(Math.abs(utcOffset), "minutes")
      .toDate();
  }

  return dayjs(unix * 1000).toDate();
}

/**
 * This function should be used within @dateChanged event in EdroneDatepicker component.
 * @param date
 */
function getUnixEndOfDay(date: Date) {
  const utcOffset = new Date(date).getTimezoneOffset();
  return dayjs(date).endOf("day").subtract(utcOffset, "minutes").unix();
}

/**
 * This function should be used within @dateChanged event in EdroneDatepicker component.
 * @param date
 */
function getUnixStartOfDayPlusOneSecond(date: Date) {
  const utcOffset = new Date(date).getTimezoneOffset();
  return dayjs(date).startOf("day").subtract(utcOffset, "minutes").add(1, "second").unix();
}

function getDateMonthAgo() {
  return dayjs(new Date()).subtract(31, "day").toDate();
}

function getDefaultDateRange() {
  const dateFrom = getDateMonthAgo();
  const currentDate = dayjs(new Date()).subtract(1, "day").toDate();

  return [dateFrom, currentDate];
}

function getDefaultDateRangeWithCurrentDay() {
  const dateFrom = getDateMonthAgo();
  const currentDate = dayjs(new Date()).toDate();

  return [dateFrom, currentDate];
}

/**
 * Function is used in the EdroneDatepicker component as the emit value.
 * EdroneDatepicker component is based on new Date() object, which is in local timezone taken from the browser.
 * The function takes a Date object in the local timezone and returns a new Date object in the timezone of the user's account.
 * It uses the dayjs library to format the input date, extract the time zone of the user's account from the window.EDRONE_CONTEXT.timezone variable, and convert the date to the account's timezone.
 */
function getDateInAccountTimezoneFromLocalTimezone(newDate: Date) {
  return dayjs.tz(dayjs(newDate).format().slice(0, 19), window.EDRONE_CONTEXT.timezone).toDate();
}

/**
 * Function is used in the EdroneDatepicker component for an internal value.
 * EdroneDatepicker component is based on new Date() object, which is in local timezone taken from the browser.
 * The function takes a Date object in a specific timezone and returns a new Date object in the local timezone.
 * It uses the dayjs library to format the input date, extract the timezone from the window.EDRONE_CONTEXT.timezone variable, and convert the date to the local timezone using the Intl.DateTimeFormat().resolvedOptions().timeZone property.
 */
function getDateInLocalTimezoneFromAccountTimezone(newDate: Date) {
  return dayjs
    .tz(
      dayjs.tz(newDate, window.EDRONE_CONTEXT.timezone).format().slice(0, 19),
      Intl.DateTimeFormat().resolvedOptions().timeZone
    )
    .toDate();
}

function disabledBeforeToday(date: Date) {
  return dayjs().subtract(1, "day").isAfter(dayjs(date, "day"));
}

function disabledAfterToday(date: Date) {
  return dayjs().isBefore(dayjs(date, "day"));
}

export function disabledDateBetween(date: Date, startDate?: Date | null): boolean {
  if (startDate) {
    return disabledAfterToday(date) || dayjs(date).isBefore(dayjs(startDate).startOf("day"));
  }
  return disabledAfterToday(date);
}

function disabledWithToday(date: Date): boolean {
  return dayjs(date).isSameOrAfter(dayjs(), "day");
}

export const disabledTodayAndBefore = (date: Date) => {
  return dayjs().isAfter(dayjs(date, "day"));
};
